import { useDebouncedSave, useSinglePromise } from '@fox/atoms/hooks/use-debounce';
import { Button } from '@fox/atoms/src/button/index';
import { MuiAlert, MuiAlertTitle } from '@fox/atoms/src/mui-alert/index';
import { MuiMenuItem } from '@fox/atoms/src/mui-list/index';
import { NumberInput } from '@fox/atoms/src/number-input/index';
import { Select } from '@fox/atoms/src/select/index';
import { Typography } from '@fox/atoms/src/typography/index';
import { formatTime } from '@fox/atoms/utils/date-functions';
import { pluralize, tryParseJSONObject } from '@fox/atoms/utils/string';
import { IBlockDefinition, IBlockProps } from '@fox/blocks/contracts/block';
import { FlexColumnSmallSpaced, FlexColumnSpaced, FlexRow, SpaceBetweenRow } from '@fox/layout/src/flex';
import { UseQueryResult, useMutation, useQuery } from '@tanstack/react-query';
import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { CREATE_PEOPLE_BLOCK, CREATE_PERSON_BLOCK, PAYMENT_BLOCK_ANSWER_TYPE, TICKET_BLOCK_TYPE } from '../../../form-block-types';
import { findRenderDependencies } from '../../../utils';
import { EventCapacityStatus, ReservationModel, ReservationStatus } from '../../form-event-context/contracts';
import { useFormEventContext } from '../../form-event-context/form-event-context';
import { useFormFinancialContext } from '../../form-financial-context/form-financial-context';
import { IField, IQuestionAnswer } from '../../form-root/contracts';
import { FormRenderMode, QuestionStyleTypes } from '../../form-runtime-context/contracts';
import { useFormRuntimeContext, useQuestionStyle } from '../../form-runtime-context/form-runtime-context';
import { RequiredAsterisk } from '../../required-asterisk/required-asterisk';
import { useFormBlockReRenderer } from '../../use-form-block-re-renderer';

import { stripTypenames } from '@fox/apollo';
import { useConditionEvaluator } from '../form-block/conditionals/helpers';
import { checkSuccessfulPayment } from '../payment-block/helpers';
import { EventTickets, TicketAnswer, TicketAnswerData, TicketDefinition, TicketFieldProps } from './contracts';
import {
	buildTicketLineItem,
	calculateOtherTicketQuota,
	calculateRemainingTickets,
	calculateTicketQuantity,
	createOptionLabel,
	findReservedTicketCount,
	reserveTickets
} from './helpers';

export function TicketBlock({ id, props, tickets }: IBlockProps<TicketFieldProps> & { tickets?: EventTickets | null }) {
	const { fetchCurrencies } = useFormFinancialContext();
	const { createTicketReservation, getTicketDetails, event, setEventCapacityData } = useFormEventContext();
	const { owner, formRenderMode, inLocalSurveyMode, getAnswers, getAnswer, answerQuestion, sections, formRequestId } = useFormRuntimeContext();
	const dependencies = findRenderDependencies(sections, [PAYMENT_BLOCK_ANSWER_TYPE, CREATE_PERSON_BLOCK, CREATE_PEOPLE_BLOCK]);
	const conditionalProps = {
		name: props.name,
		metadataJson: JSON.stringify({ conditionalDefinitions: props.conditionalDefinitions })
	} as IField; //this is a hack because the ticket block doesnt store this information in the metadataJson
	useFormBlockReRenderer([props.name, ...dependencies], conditionalProps);
	const token = (window as any).validatedFormToken;
	const labelStyle = useQuestionStyle(QuestionStyleTypes.label, {}, id);
	const answerStyle = useQuestionStyle(QuestionStyleTypes.answer, {}, id);
	const currentAnswer: IQuestionAnswer<TicketAnswerData> = getAnswer(props.name) ?? {
		questionName: props.name,
		answer: {}
	};
	const ticketReservation = useRef(currentAnswer.data?.reservation);
	const parsedAnswer = tryParseJSONObject<TicketAnswer>(currentAnswer.answer?.answerJson, {});
	const answerRef = useRef(parsedAnswer);
	const canFetch = (!!token || !!inLocalSurveyMode) && !!owner?.ownerId && formRenderMode !== FormRenderMode.design;
	const [quantity, setQuantity] = useState(parsedAnswer?.quantity);

	const reserveTicketMutation = useMutation({
		mutationFn: ({
			eventId,
			ticketId,
			count,
			reservationId,
			token,
			formRequestId
		}: {
			formRequestId: string;
			eventId: string;
			ticketId?: string;
			count?: number;
			reservationId?: string;
			enforceCount?: boolean;
			token?: string;
		}) => createTicketReservation({ formRequestId, eventId, ticketId, count, reservationId, token })
	});

	const { data, isFetching, isLoading, isFetched, refetch } = useQuery({
		queryKey: ['available_tickets', props.allowedTickets, canFetch],
		queryFn: async () => {
			const result = await getTicketDetails({ eventId: owner!.ownerId!, allowedTickets: props.allowedTickets });
			if (result?.status === 'success') {
				setEventCapacityData(prev => ({ ...(prev ?? {}), capacityRemaining: result?.availableQuotaRemaining }));
				return result;
			}
		},
		enabled: canFetch,
		staleTime: Infinity
	});

	const { data: currencies, isLoading: currenciesLoading } = useQuery({
		queryKey: ['fetchCurrencies'],
		queryFn: async () => {
			const result = await fetchCurrencies();
			if (!result?.default) throw Error('Unable to fetch supported currencies');
			return result;
		},
		staleTime: Infinity,
		enabled: formRenderMode !== FormRenderMode.design
	});

	const defaultCurrency = currencies?.default;
	const availableQuotaRemaining = data?.availableQuotaRemaining;

	const allTickets =
		tickets?.definitions?.filter(ticket => props.allowedTickets?.some(t => t === ticket.id) && ticket.public) ?? data?.tickets ?? [];

	const availableTickets: TicketDefinition[] = allTickets?.filter((t, _, self) => {
		if (t.ticketCounts) {
			const ticketLimit = t.ticketCounts.limit; // already multiplied for groups
			if (t.type !== 'group') {
				// Don't need to check quota as if no quota available ticket block doesn't render selections
				const reserved = findReservedTicketCount(ticketReservation.current, t?.id);
				const ticketsCurrent = t.ticketCounts.current - reserved;
				const hasTicketsAvailable = ticketLimit > ticketsCurrent;
				return hasTicketsAvailable;
			} else {
				// We want to add the current reservation amount to availableQuota regardless of ticket since they are still for this user
				const reserved = findReservedTicketCount(ticketReservation.current, t?.id, true);
				const reservationMultiplier = self?.find(tick => tick.id === ticketReservation.current?.fieldId)?.groupMultiplier ?? 1;
				const reservedMultiplied = reserved * reservationMultiplier;
				// Infinity - this is a way of saying that we want to use quota only if enabled
				const trueQuotaAvailable = (availableQuotaRemaining ?? Infinity) + reservedMultiplied;
				const hasQuotaAvailable = trueQuotaAvailable >= (t.groupMultiplier ?? 1);
				const hasTicketsAvailable = ticketLimit - (t.ticketCounts.current - reserved) > 0;
				return hasQuotaAvailable && hasTicketsAvailable;
			}
		} else return false;
	});

	const selectedTicket = availableTickets?.find(t => t.id === answerRef?.current?.ticketId);
	const otherQuotaReserved = calculateOtherTicketQuota(allTickets, selectedTicket, ticketReservation.current);

	const selectedTicketRemainingTickets = calculateRemainingTickets(
		selectedTicket,
		ticketReservation.current,
		availableQuotaRemaining,
		otherQuotaReserved
	);
	const allowQuantitySelection = props.allowQuantitySelection;

	const conditionValid = useConditionEvaluator(conditionalProps);
	const answers = getAnswers();
	const quantityShouldBe = calculateTicketQuantity(
		answers ?? [],
		sections,
		selectedTicket,
		ticketReservation.current,
		availableQuotaRemaining,
		otherQuotaReserved
	);
	const hasSuccessfulPayment = checkSuccessfulPayment(answers ?? []);

	useEffect(() => {
		if (formRenderMode === FormRenderMode.design || !conditionValid || isFetching || !selectedTicket || hasSuccessfulPayment) return;
		const handleFillAnswer = async newAnswer => {
			await fillAnswer(newAnswer);
		};
		// If ticket quantity is automatic then re-calculate it when form answers change incase a new person was added with multi people block
		if (!allowQuantitySelection) {
			if (answerRef.current?.quantity !== quantityShouldBe || answerRef.current?.ticketId !== selectedTicket?.id) {
				handleFillAnswer({ quantity: quantityShouldBe, ticketId: selectedTicket?.id });
			}
		}
	}, [quantityShouldBe, formRenderMode, conditionValid, selectedTicket]);

	const reserveSingle = useSinglePromise(reserveTickets);
	const fillAnswer = async (newAnswer: TicketAnswer) => {
		if (formRenderMode === FormRenderMode.design) {
			return;
		}
		answerRef.current = { ...answerRef.current, ...newAnswer };
		const reserveTicketFunction = async (input: {
			eventId: string;
			formRequestId: string;
			ticketId?: string;
			count?: number;
			reservationId?: string;
			enforceCount?: boolean;
			token?: string;
		}) => {
			const { eventId, ticketId, count, reservationId, token } = input;
			return await reserveTicketMutation.mutateAsync({
				formRequestId,
				token,
				eventId,
				ticketId,
				count,
				reservationId
			});
		};

		const { reservation, isCapped } = await reserveSingle({
			token,
			newAnswer: answerRef.current,
			reservation: ticketReservation,
			reserveTicketMutation: reserveTicketFunction,
			eventId: owner?.ownerId,
			refetch,
			formRequestId
		});
		if (isCapped) {
			answerRef.current = { ...answerRef.current, quantity: reservation?.count };
		}
		setQuantity(answerRef.current?.quantity);
		answerQuestion({
			answer: {
				answerJson: JSON.stringify(answerRef.current)
			},
			questionName: props.name,
			data: {
				lineItems: buildTicketLineItem(availableTickets, answerRef.current),
				reservation: stripTypenames(reservation)
			},
			type: TICKET_BLOCK_TYPE
		});
	};
	const designModeNoTicketsSetup = formRenderMode === FormRenderMode.design && !tickets?.definitions;
	const designModeNoAvailableTickets = formRenderMode === FormRenderMode.design && availableTickets.length === 0;
	const designRunTimeError = formRenderMode === FormRenderMode.preview && !tickets?.definitions;
	const designRunTimeNoTickets = formRenderMode === FormRenderMode.preview && availableTickets.length === 0;
	const alreadyProcessed = hasSuccessfulPayment || ticketReservation?.current?.status === ReservationStatus.Confirmed;
	const publicError = formRenderMode === FormRenderMode.runtime && isFetched && !data?.tickets && canFetch;
	const publicNoTickets = formRenderMode === FormRenderMode.runtime && isFetched && availableTickets.length === 0 && canFetch;
	const publicLoading = formRenderMode === FormRenderMode.runtime && (isFetching || isLoading || currenciesLoading);

	const maxTickets = selectedTicketRemainingTickets ? Math.min(props.max, selectedTicketRemainingTickets) : props.max;
	const ticketsRemaining = Math.min(maxTickets, selectedTicketRemainingTickets || 0);

	const [_, setDebounced] = useDebouncedSave<number>(
		parsedAnswer?.quantity || 0,
		async (val: number) => {
			let newQuantity = typeof val === 'number' ? val : undefined;
			if (
				typeof newQuantity === 'number' &&
				((selectedTicketRemainingTickets && newQuantity > selectedTicketRemainingTickets) || newQuantity > maxTickets)
			) {
				newQuantity = ticketsRemaining;
			}
			if (newQuantity !== answerRef.current?.quantity || ticketReservation.current?.status === ReservationStatus.Expired) {
				await fillAnswer({ quantity: newQuantity });
			}
		},
		1000
	);

	if (!conditionValid) return null;
	if (alreadyProcessed) {
		return (
			<MuiAlert severity="success">
				<Typography>Tickets have been processed and can no longer be changed.</Typography>
				{ticketReservation.current?.status === ReservationStatus.Confirmed ? (
					<>
						<Typography>Ticket type: {selectedTicket?.name} </Typography>
						<Typography>Quantity: {answerRef.current?.quantity} </Typography>
					</>
				) : null}
			</MuiAlert>
		);
	}
	if (designModeNoTicketsSetup) {
		return <MuiAlert severity="info">Event has no tickets</MuiAlert>;
	}
	if (designModeNoAvailableTickets) {
		return <MuiAlert severity="info">No available tickets selected.</MuiAlert>;
	}
	if (publicError) return <MuiAlert severity="error">Error loading available tickets</MuiAlert>;
	if (publicNoTickets || designRunTimeNoTickets) return <MuiAlert severity="info">No tickets available.</MuiAlert>;
	if (designRunTimeError) {
		return <MuiAlert severity="error">Error loading available tickets</MuiAlert>;
	}

	const canBuyTickets =
		formRenderMode === FormRenderMode.preview || event?.status === EventCapacityStatus.Available || event?.status === EventCapacityStatus.NoLimit;
	const quantityError = (quantity ?? 0) > ticketsRemaining && !publicLoading;
	return (
		<FlexColumnSpaced style={{ pointerEvents: formRenderMode !== FormRenderMode.design ? 'all' : 'none' }}>
			<Typography sx={labelStyle} variant="h6">
				Tickets {props.required ? <RequiredAsterisk /> : null}
			</Typography>

			<SpaceBetweenRow style={{ gap: '1rem' }}>
				{availableTickets.length > 0 || publicLoading ? (
					<Select<TicketDefinition, false, false, false>
						size="small"
						options={isFetching ? [] : availableTickets}
						getOptionLabel={
							option => createOptionLabel(option, defaultCurrency) // This is in cents
						}
						value={selectedTicket}
						key={selectedTicket?.id}
						label={'Select tickets'}
						multiple={false}
						onChange={async (_, value, reason) => {
							const newAnswer: { ticketId: string | undefined; quantity?: number } = {
								ticketId: value?.id,
								quantity: answerRef.current?.quantity
							};
							const otherQuotaReserved = calculateOtherTicketQuota(allTickets, value, ticketReservation.current);

							const remainingTickets = calculateRemainingTickets(
								value,
								ticketReservation.current,
								availableQuotaRemaining ?? 0,
								otherQuotaReserved
							);

							if (!allowQuantitySelection) {
								newAnswer.quantity = calculateTicketQuantity(
									answers,
									sections,
									undefined,
									ticketReservation.current,
									availableQuotaRemaining,
									otherQuotaReserved
								);
							} else if (
								typeof remainingTickets === 'number' &&
								typeof answerRef.current?.quantity === 'number' &&
								answerRef.current?.quantity > remainingTickets
							) {
								newAnswer.quantity = remainingTickets;
							}
							if (reason === 'clear') {
								newAnswer.quantity = 0;
							}
							await fillAnswer(newAnswer);
						}}
						fullWidth
						helperText={allowQuantitySelection ? ' ' : undefined} // hacky alignment styling
						status={reserveTicketMutation.isLoading || isFetching ? 'loading' : undefined}
						loading={isFetching || isLoading}
						disabled={!canBuyTickets || reserveTicketMutation.isLoading || isFetching}
						renderOption={(props, option) => {
							const label = createOptionLabel(option, defaultCurrency);

							return (
								<MuiMenuItem {...props} key={option.id}>
									<FlexColumnSmallSpaced>
										<Typography>{label}</Typography>
										<Typography variant="caption">{option.description}</Typography>
									</FlexColumnSmallSpaced>
								</MuiMenuItem>
							);
						}}
					/>
				) : null}

				{availableTickets?.length > 0 && allowQuantitySelection ? (
					<NumberInput
						fullWidth
						style={{ maxWidth: '8rem' }}
						clamp={true}
						step={1}
						fixedDecimals={0}
						label="Quantity"
						value={quantity}
						min={props.min ?? 0}
						status={quantityError ? 'error' : undefined}
						helperText={quantityError ? `Max avail: ${ticketsRemaining}` : ' '}
						disabled={!canBuyTickets || reserveTicketMutation.isLoading || isFetching}
						onChange={val => {
							setQuantity(val || undefined);
							setDebounced(val || 0);
						}}
					/>
				) : availableTickets?.length > 0 ? (
					<FlexRow>
						<Typography sx={labelStyle}>Quantity: </Typography>
						<Typography sx={answerStyle}> {answerRef.current?.quantity ?? 0}</Typography>
					</FlexRow>
				) : null}
			</SpaceBetweenRow>
			{formRenderMode !== FormRenderMode.design ? (
				<TicketReservationStatus
					isLoading={reserveTicketMutation.isLoading}
					selectedTicket={selectedTicket}
					quantity={quantity}
					currentAnswer={currentAnswer}
					fillAnswer={fillAnswer}
					selectedTicketRemainingTickets={ticketsRemaining}
					refetchTickets={refetch}
					key={`${ticketReservation.current?.id}`}
					reservationRef={ticketReservation}
				/>
			) : null}
		</FlexColumnSpaced>
	);
}

const TicketReservationStatus = ({
	fillAnswer,
	isLoading,
	currentAnswer,
	selectedTicket,
	quantity,
	selectedTicketRemainingTickets,
	refetchTickets,
	reservationRef
}: {
	fillAnswer: (newAnswer: TicketAnswer) => Promise<void>;
	refetchTickets: (options?: { throwOnError: boolean; cancelRefetch: boolean }) => Promise<UseQueryResult>;
	isLoading: boolean;
	currentAnswer: IQuestionAnswer<TicketAnswerData>;
	selectedTicket?: TicketDefinition;
	quantity?: number;
	selectedTicketRemainingTickets?: number;
	reservationRef: MutableRefObject<ReservationModel | undefined>;
}) => {
	const { expireTicketReservation } = useFormEventContext();
	const { answerQuestion } = useFormRuntimeContext();
	const token = (window as any).validatedFormToken;
	const endTime = reservationRef.current?.expiry;
	const [remainingTime, setRemainingTime] = useState(() => {
		if (!endTime) return null;
		const currentTime = new Date().getTime();
		const end = new Date(endTime).getTime();
		return end - currentTime;
	});
	const selectedMaxQuantity =
		typeof selectedTicketRemainingTickets === 'number' && typeof quantity === 'number' && quantity >= selectedTicketRemainingTickets;

	useEffect(() => {
		if (!endTime) return;
		const intervalId = setInterval(() => {
			const currentTime = new Date().getTime();
			const end = new Date(endTime).getTime();

			if (currentTime >= end) {
				setRemainingTime(0);
				clearInterval(intervalId);
				markReservationExpired();
				return;
			}
			const remaining = end - currentTime;
			setRemainingTime(remaining);
		}, 1000);

		return () => clearInterval(intervalId);
	}, [endTime]);

	const markReservationExpired = async () => {
		if (reservationRef.current?.id && reservationRef?.current?.owner?.id) {
			const expiredReservation = await expireTicketReservation({
				eventId: reservationRef.current.owner.id,
				reservationId: reservationRef.current.id,
				token
			});
			reservationRef.current = expiredReservation;
			answerQuestion({
				...currentAnswer,
				data: { ...currentAnswer.data, reservation: expiredReservation }
			});
			await refetchTickets();
		}
	};
	if (isLoading) {
		return (
			<MuiAlert severity="info">
				<MuiAlertTitle>Ticket reservation status</MuiAlertTitle>
				<p>Updating ticket reservation</p>
			</MuiAlert>
		);
	} else if (reservationRef.current?.status === ReservationStatus.Expired) {
		return (
			<MuiAlert severity="error">
				<MuiAlertTitle>Ticket reservation status</MuiAlertTitle>
				<p>Ticket reservation expired</p>
				<Button variant="primary" loading={isLoading} onClick={async () => await fillAnswer({ ticketId: selectedTicket?.id, quantity })}>
					Check for new tickets
				</Button>
			</MuiAlert>
		);
	} else if (reservationRef.current?.id || !token) {
		const ticketQuantity = selectedMaxQuantity ? selectedTicketRemainingTickets : quantity;
		return remainingTime || selectedMaxQuantity ? (
			<MuiAlert severity="info">
				<MuiAlertTitle>Ticket reservation status</MuiAlertTitle>
				{selectedTicket?.type === 'group' && ticketQuantity ? (
					<Typography>
						You are buying {ticketQuantity * (selectedTicket?.groupMultiplier ?? 1)} tickets ({ticketQuantity} x{' '}
						{pluralize(ticketQuantity, { single: 'group', plural: 'groups' })} of {selectedTicket?.groupMultiplier ?? 1}){' '}
					</Typography>
				) : null}
				{remainingTime && !isNaN(remainingTime) && remainingTime > 0 ? (
					<p>{`Ticket reservation expires in: ${formatTime(remainingTime)}`}</p>
				) : null}
				{selectedMaxQuantity && selectedTicket ? (
					<p>{`Only ${selectedTicketRemainingTickets} ${selectedTicket?.name} tickets available.`}</p>
				) : null}
			</MuiAlert>
		) : null;
	} else {
		return null;
	}
};

export const TicketBlockRuntime: IBlockDefinition = {
	type: TICKET_BLOCK_TYPE,
	Component: TicketBlock
};
